import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Globe, Languages } from "lucide-react";

export default function StepStranieri({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const [lingue, setLingue] = useState(d._lingue || "italiano, inglese");
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/stranieri", {
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
        numero_stranieri: s1.numero_stranieri,
        lingue_presenti: lingue.split(",").map((x) => x.trim()).filter(Boolean),
        note,
      }, { timeout: 120000 });
      onChange({ ...r.data, _lingue: lingue, _note: note });
      toast.success("Valutazione lavoratori stranieri completata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="step-stranieri">
      <p className="text-sm text-muted-foreground">
        Valutazione ex <b>D.Lgs. 81/08 art. 36-37</b>: informazione e formazione in lingua
        comprensibile per il lavoratore. Stranieri in azienda: <b>{s1.numero_stranieri || 0}</b>.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold">Lingue presenti in azienda (separate da virgola)</label>
          <Input value={lingue} onChange={(e) => setLingue(e.target.value)} data-testid="str-lingue" />
        </div>
        <div>
          <label className="text-xs font-semibold">Note</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="es. area produzione con >50% stranieri…" data-testid="str-note" />
        </div>
      </div>
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="str-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
        Valuta gestione stranieri
      </Button>

      {d.lingue_da_supportare?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Languages className="w-3 h-3" /> Lingue da supportare
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {d.lingue_da_supportare.map((l, i) => (
              <span key={`lang-${l || i}`} className="text-xs px-2 py-0.5 rounded border border-border bg-muted/30">{l}</span>
            ))}
          </div>
        </div>
      )}

      {d.misure_organizzative?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm">Misure organizzative</h3>
          <ul className="text-sm mt-1 space-y-1">
            {d.misure_organizzative.map((m, i) => (
              <li key={`mis-${i}-${(m.misura || "").slice(0, 20)}`} className="border-l-2 border-primary/30 pl-2">
                {m.misura}
                <div className="text-[11px] text-muted-foreground">Rif: {m.normativa}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.verifica_apprendimento && (
        <div className="border border-border rounded-md p-3 bg-muted/20 text-sm">
          <b>Verifica apprendimento:</b> {d.verifica_apprendimento.modalita}
          <div className="text-[11px] text-muted-foreground">Rif: {d.verifica_apprendimento.normativa}</div>
        </div>
      )}

      {d.cultura_sicurezza?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm">Aspetti culturali</h3>
          <ul className="text-sm mt-1 space-y-0.5">
            {d.cultura_sicurezza.map((c, i) => <li key={`cs-${i}-${(c || "").slice(0, 20)}`}>• {c}</li>)}
          </ul>
        </div>
      )}

      {d.check_permesso_soggiorno && (
        <div className={`rounded-md p-3 text-sm ${d.check_permesso_soggiorno.obbligo ? "border border-amber-300 bg-amber-50" : "border border-border bg-muted/20"}`}>
          <b>Verifica permesso di soggiorno:</b> {d.check_permesso_soggiorno.obbligo ? "obbligatoria" : "non richiesta"}
          {d.check_permesso_soggiorno.controllo_periodico && <div className="text-xs mt-0.5">Periodicità: {d.check_permesso_soggiorno.controllo_periodico}</div>}
          <div className="text-[11px] text-muted-foreground mt-0.5">Rif: {d.check_permesso_soggiorno.normativa}</div>
        </div>
      )}

      {d.documenti_specifici?.length > 0 && (
        <div className="text-xs text-muted-foreground">Documenti specifici: {d.documenti_specifici.join(" · ")}</div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
